import * as React from 'react'

import { MobXProviderContext } from 'mobx-react'

import { PropertyType } from '~/client/graph/types.generated'
import {
  ResourceTypeId,
  RESOURCE_ROOT_FIELDS,
  RESOURCE_STATIC_FIELDS,
  StaticFieldConfig,
} from '~/client/src/shared/constants/resourceTypes'
import { useCanEditResource } from '~/client/src/shared/hooks/useCanEditResource'
import UserProject from '~/client/src/shared/models/UserProject'
import { buildResourceUiConfig } from '~/client/src/shared/resources/builders/buildResourceUiConfig'
import { getResourceDefinition } from '~/client/src/shared/resources/definitions'
import { creatableLocationKinds } from '~/client/src/shared/resources/domain/creatableLocationKinds'
import {
  isProjectLocationKind,
  LocationKind,
  LOCATION_KIND_PROPERTY,
  rootOfKind,
} from '~/client/src/shared/resources/domain/locationKind'
import { getResourceLinkTargetByType } from '~/client/src/shared/resources/resourceLink/registry'
import { classifyResourcePropertyInput } from '~/client/src/shared/resources/resourcePropertyInput'
import ProjectDateStore from '~/client/src/shared/stores/ui/ProjectDate.store'
import {
  buildAncestorChain,
  buildOptionsByValue,
  getVisibleOptions,
  type HierarchyTreeOption,
  parseHierarchyOptions,
} from '~/client/src/shared/utils/hierarchyTree'

import Localization from '../../localization/LocalizationManager'
import {
  parseDateValue,
  toMidnightTimestamp,
} from '../../utils/dateValueParser'
import {
  CheckboxFieldConfig,
  CompanyFieldConfig,
  DateTimeFieldConfig,
  FieldConfig,
  FieldType,
  FileFieldConfig,
  HierarchyFieldConfig,
  HierarchyFormOption,
  NumberFieldConfig,
  SelectFieldConfig,
  SelectOption,
  TextAreaFieldConfig,
  TextFieldConfig,
  UserFieldConfig,
} from '../DynamicForm/types'
import { iconCatalogFor } from '../IconPicker'

interface TemplateProperty {
  propertyId?: string | null
  orderIndex?: number | null
  isRequired?: boolean | null
  isHidden?: boolean | null
  defaultValue?: any
}

interface PropertyDefinition {
  id?: string | null
  resourceTypeId?: string | null
  name?: string | null
  displayName?: string | null
  propertyType?: string | null
  position?: number | null
  configuration?: any
}

const toResourceDateTimestamp = (
  value: any,
  projectDateStore?: ProjectDateStore,
): number | undefined => {
  const date = parseDateValue(value, projectDateStore)
  return date ? toMidnightTimestamp(date, projectDateStore) : undefined
}

const normalizeHierarchyValue = (value: unknown): string | null =>
  value == null ? null : String(value)

/**
 * A project has exactly one Project Location — the site root it is created with — so
 * offering the kind as a type would let any location be saved as a second one. Filtered
 * out of the picker rather than out of the hierarchy: the site root itself still has to
 * render its own type, which is why the selected value always survives.
 */
const withoutUnofferedKinds = <T extends { value: string }>(
  options: T[],
  selectedValue: string | null,
): T[] =>
  options.filter(
    option =>
      option.value === selectedValue ||
      rootOfKind(option.value) !== LocationKind.ProjectLocation,
  )

/**
 * The kinds a new location can be created as, with the parents the picker drills through to them,
 * deleted ones too: the server takes a live kind under a deleted parent.
 */
const withCreatableKinds = <T extends HierarchyTreeOption>(
  options: T[],
  isCreatable: (kind: string) => boolean,
): T[] => {
  const optionsByValue = buildOptionsByValue(options)
  const kept = new Set(
    options
      .filter(option => isCreatable(option.value))
      .flatMap(option => buildAncestorChain(option.value, optionsByValue))
      .map(option => option.value),
  )
  return options.filter(option => kept.has(option.value))
}

const assertNever = (value: never): never => {
  throw new Error(`Unhandled editable property input: ${String(value)}`)
}

const transformPropertyToField = (
  templateProperty: TemplateProperty,
  propertyDef: PropertyDefinition,
  recordData: any,
  readOnlyPropertyNames: Set<string>,
  allOptional: boolean,
  projectDateStore?: ProjectDateStore,
  recordId?: string | null,
  resourceTypeId?: string,
  isPreview?: boolean,
): FieldConfig | null => {
  if (!propertyDef?.name || !propertyDef.propertyType) return null
  const propertyInput = classifyResourcePropertyInput(propertyDef)
  if (propertyInput.kind !== 'editable-field') {
    return null
  }

  const fieldValue = recordData?.[propertyDef.name]
  const hasFieldValue = Object.prototype.hasOwnProperty.call(
    recordData || {},
    propertyDef.name,
  )
  const baseConfig = {
    name: propertyDef.name,
    label: propertyDef.displayName || propertyDef.name,
    required: allOptional ? false : !!templateProperty.isRequired,
    readOnly: readOnlyPropertyNames.has(propertyDef.name),
  }
  const propertyType = propertyInput.propertyType

  switch (propertyType) {
    case PropertyType.Text: {
      if (propertyDef.configuration?.multiline) {
        const textAreaConfig: TextAreaFieldConfig = {
          ...baseConfig,
          type: FieldType.TEXTAREA,
          defaultValue: fieldValue
            ? String(fieldValue)
            : templateProperty.defaultValue || '',
          placeholder: Localization.translator.enterX(baseConfig.label),
        }
        return textAreaConfig
      }

      const textConfig: TextFieldConfig = {
        ...baseConfig,
        type: FieldType.TEXT,
        defaultValue: fieldValue
          ? String(fieldValue)
          : templateProperty.defaultValue || '',
        placeholder: Localization.translator.enterX(baseConfig.label),
      }
      return textConfig
    }

    case PropertyType.Number: {
      const numberConfig: NumberFieldConfig = {
        ...baseConfig,
        type: FieldType.NUMBER,
        defaultValue:
          fieldValue != null && fieldValue !== ''
            ? Number(fieldValue)
            : templateProperty.defaultValue,
        placeholder: Localization.translator.enterX(baseConfig.label),
      }
      return numberConfig
    }

    case PropertyType.Select: {
      const options: SelectOption[] = []
      const isCreationMode = recordId === 'new'

      for (const parsedOption of parseHierarchyOptions(
        propertyDef.configuration?.options,
      )) {
        const shouldIncludeOption = isCreationMode
          ? !parsedOption.deleted
          : !parsedOption.deleted || parsedOption.value === fieldValue

        if (shouldIncludeOption) {
          const { parentValue: _parentValue, ...selectOption } = parsedOption
          options.push(selectOption)
        }
      }

      const includeNoneOption = !!propertyDef.configuration?.includeNoneOption
      const finalOptions = includeNoneOption
        ? [{ value: null, label: Localization.translator.none }, ...options]
        : options

      const selectConfig: SelectFieldConfig = {
        ...baseConfig,
        type: FieldType.SELECT,
        options: finalOptions,
        defaultValue: fieldValue || templateProperty.defaultValue || undefined,
        placeholder: Localization.translator.selectX(baseConfig.label),
        allowUserCreation: !!propertyDef.configuration?.allowCustomOptions,
        includeNoneOption,
      }
      return selectConfig
    }

    case PropertyType.Hierarchy: {
      const parsedOptions = parseHierarchyOptions(
        propertyDef.configuration?.options,
      ) as HierarchyFormOption[]

      const isLocationKind =
        resourceTypeId === ResourceTypeId.LOCATIONS &&
        propertyDef.name === LOCATION_KIND_PROPERTY
      // Creating, the server takes only a kind the project can create, so a seeded or default
      // kind it cannot is dropped rather than kept as the selected value an edit keeps.
      const isCreatable =
        isLocationKind && recordId === 'new' && !isPreview
          ? creatableLocationKinds(propertyDef.configuration?.options)
          : null
      const isOffered = (value: unknown) =>
        !isCreatable || isCreatable(normalizeHierarchyValue(value))
      const normalizedValue = normalizeHierarchyValue(fieldValue)
      const selectedValue =
        hasFieldValue && isOffered(normalizedValue) ? normalizedValue : null
      const visibleOptions = isCreatable
        ? withCreatableKinds(parsedOptions, isCreatable)
        : getVisibleOptions(parsedOptions, selectedValue)

      const hierarchyConfig: HierarchyFieldConfig = {
        ...baseConfig,
        type: FieldType.HIERARCHY,
        options:
          isLocationKind && !isCreatable
            ? withoutUnofferedKinds(visibleOptions, selectedValue)
            : visibleOptions,
        defaultValue: hasFieldValue
          ? selectedValue
          : isOffered(templateProperty.defaultValue)
            ? (templateProperty.defaultValue ?? undefined)
            : undefined,
        placeholder: Localization.translator.selectX(baseConfig.label),
      }

      // A project has exactly one site root and its kind is not a choice, so the picker is
      // locked shut. `disabled` rather than `readOnly`: readOnly pulls the field out of the
      // form entirely and reprints it as static text at the top of the card, while disabled
      // leaves it in place, still showing "Project Location", with BaseStruxhubSelector
      // refusing to open it.
      if (
        resourceTypeId === ResourceTypeId.LOCATIONS &&
        propertyDef.name === LOCATION_KIND_PROPERTY &&
        isProjectLocationKind(selectedValue)
      ) {
        hierarchyConfig.disabled = true
      }

      return hierarchyConfig
    }

    case PropertyType.Datetime: {
      const resourceValue = toResourceDateTimestamp(
        fieldValue,
        projectDateStore,
      )
      const templateDefaultValue = toResourceDateTimestamp(
        templateProperty.defaultValue,
        projectDateStore,
      )
      const dateTimeConfig: DateTimeFieldConfig = {
        ...baseConfig,
        type: FieldType.DATETIME,
        defaultValue: resourceValue ?? templateDefaultValue,
        placeholder: Localization.translator.selectDate,
      }
      return dateTimeConfig
    }

    case PropertyType.Checkbox: {
      const toBool = (v: any): boolean => v === true || v === 'true'
      const resolvedValue =
        fieldValue != null
          ? toBool(fieldValue)
          : templateProperty.defaultValue != null
            ? toBool(templateProperty.defaultValue)
            : false

      const checkboxConfig: CheckboxFieldConfig = {
        ...baseConfig,
        type: FieldType.CHECKBOX,
        defaultValue: resolvedValue,
      }
      return checkboxConfig
    }

    case PropertyType.User: {
      const userConfig: UserFieldConfig = {
        ...baseConfig,
        type: FieldType.USER,
        defaultValue: fieldValue ? String(fieldValue) : undefined,
      }
      return userConfig
    }

    case PropertyType.Company: {
      const companyConfig: CompanyFieldConfig = {
        ...baseConfig,
        type: FieldType.COMPANY,
        defaultValue: fieldValue ? String(fieldValue) : undefined,
      }
      return companyConfig
    }

    case PropertyType.ResourceLink: {
      const target = getResourceLinkTargetByType(propertyInput.targetType)
      return target.buildFieldConfig({ baseConfig, fieldValue })
    }

    case PropertyType.File: {
      const existingFiles = fieldValue
        ? Array.isArray(fieldValue)
          ? fieldValue
          : [fieldValue]
        : []

      const fileConfig: FileFieldConfig = {
        ...baseConfig,
        type: FieldType.FILE,
        defaultValue: existingFiles,
        maxFiles: propertyDef.configuration?.maxFiles ?? undefined,
        acceptedFileTypes:
          propertyDef.configuration?.acceptedFileTypes ?? undefined,
        maxFileSizeMb: propertyDef.configuration?.maxFileSizeMb ?? undefined,
        placeholder: Localization.translator.attachFile,
      }
      return fileConfig
    }

    default: {
      return assertNever(propertyType)
    }
  }
}

export interface UseResourceFieldsParams {
  record?: {
    id?: string | null
    name?: string | null
    templateId?: string | null
    data?: Record<string, any> | null
  } | null
  template?: { properties?: TemplateProperty[] | null } | null
  propertyDefinitions?: PropertyDefinition[]
  recordId?: string | null
  resourceTypeId?: string
  projectDateStore?: ProjectDateStore
  fieldOptions?: {
    includeStaticFields?: boolean
    allOptional?: boolean
    includeAllProperties?: boolean
    optionalTemplateRequirements?: boolean
    /**
     * This form is rendered inside Maps. Creation surfaces outside Maps drop the
     * resource's `mapsOnlyRootFields` — Parent, for equipment — because the value
     * comes from where the item is drawn. Editing keeps them either way.
     */
    isMapsSurface?: boolean
    /** Root fields shown and required even where they would be dropped. Keep stable. */
    requiredRootFields?: readonly string[]
    /**
     * Shows each property as it is configured rather than as a new record may fill it: a new
     * location's Type is not narrowed to the kinds the project can create.
     */
    isPreview?: boolean
  }
}

/** What a picker's creation panel may ask of the form it builds. */
export type ICreationFieldOptions = Pick<
  NonNullable<UseResourceFieldsParams['fieldOptions']>,
  'requiredRootFields'
>

const transformStaticFieldToFieldConfig = (
  staticField: StaticFieldConfig,
  record: UseResourceFieldsParams['record'],
  readOnlyPropertyNames: Set<string>,
  allOptional: boolean,
): FieldConfig => {
  const fieldValue = record?.[staticField.name as keyof typeof record]

  switch (staticField.type) {
    case 'text': {
      const textConfig: TextFieldConfig = {
        name: staticField.name,
        label: staticField.label,
        type: FieldType.TEXT,
        required: allOptional ? false : staticField.required || false,
        readOnly:
          staticField.readOnly || readOnlyPropertyNames.has(staticField.name),
        defaultValue: fieldValue ? String(fieldValue) : '',
        placeholder:
          staticField.placeholder ||
          Localization.translator.enterX(staticField.label),
      }
      return textConfig
    }
    case 'number': {
      const numberConfig: NumberFieldConfig = {
        name: staticField.name,
        label: staticField.label,
        type: FieldType.NUMBER,
        required: allOptional ? false : staticField.required || false,
        readOnly:
          staticField.readOnly || readOnlyPropertyNames.has(staticField.name),
        defaultValue: fieldValue ? Number(fieldValue) : undefined,
        placeholder:
          staticField.placeholder ||
          Localization.translator.enterX(staticField.label),
      }
      return numberConfig
    }
    default: {
      const defaultConfig: TextFieldConfig = {
        name: staticField.name,
        label: staticField.label,
        type: FieldType.TEXT,
        required: allOptional ? false : staticField.required || false,
        readOnly:
          staticField.readOnly || readOnlyPropertyNames.has(staticField.name),
        defaultValue: fieldValue ? String(fieldValue) : '',
      }
      return defaultConfig
    }
  }
}

// Metadata for the Location root columns (equipment color/icon/parent) surfaced
// as always-visible, optional form fields right after the static `name`. Shared
// with FormPropertiesShell so the field list + labels have a single source.
// `defaultValue` reads `record[name]` uniformly; only type/label/rowGroup vary.
export const ROOT_FIELD_META: Record<
  string,
  {
    fieldType: FieldType
    getLabel: () => string
    /** Read inside the call, not at module scope, so it follows a language change. */
    getLabelTooltip?: () => string
    rowGroup?: string
  }
> = {
  iconName: {
    fieldType: FieldType.ICON,
    getLabel: () => Localization.translator.icon,
    rowGroup: 'iconColor',
  },
  color: {
    fieldType: FieldType.COLOR,
    getLabel: () => Localization.translator.color,
    rowGroup: 'iconColor',
  },
  parent: {
    fieldType: FieldType.PARENT,
    getLabel: () => Localization.translator.lbsParent,
    getLabelTooltip: () => Localization.translator.lbsParentDescription,
  },
}

const buildRootFieldConfig = (
  name: string,
  record: UseResourceFieldsParams['record'],
  resourceTypeId?: string,
): FieldConfig | null => {
  const meta = ROOT_FIELD_META[name]
  if (!meta) return null
  return {
    name,
    type: meta.fieldType,
    label: meta.getLabel(),
    labelTooltip: meta.getLabelTooltip?.(),
    required: false,
    alwaysVisible: true,
    rowGroup: meta.rowGroup,
    defaultValue: (record as any)?.[name] ?? null,
    // Locations and equipment draw from different glyph sets; the icon field itself is
    // shared, so the set has to be chosen here where the resource type is known.
    ...(meta.fieldType === FieldType.ICON
      ? {
          catalog: iconCatalogFor(resourceTypeId !== ResourceTypeId.LOCATIONS),
          // Only a location's `type` is its kind; equipment's names its own taxonomy.
          ...(resourceTypeId === ResourceTypeId.LOCATIONS
            ? { kindFieldName: LOCATION_KIND_PROPERTY }
            : {}),
        }
      : {}),
    ...(meta.fieldType === FieldType.COLOR
      ? { iconFieldName: 'iconName' }
      : {}),
    ...(meta.fieldType === FieldType.PARENT ? { objectId: record?.id } : {}),
  } as FieldConfig
}

const buildRootFieldConfigs = (
  resourceTypeId: string | undefined,
  record: UseResourceFieldsParams['record'],
  excludedNames: ReadonlySet<string>,
): FieldConfig[] => {
  if (!resourceTypeId) return []
  return (RESOURCE_ROOT_FIELDS[resourceTypeId] || [])
    .filter(name => !excludedNames.has(name))
    .map(name => buildRootFieldConfig(name, record, resourceTypeId))
    .filter((f): f is FieldConfig => f !== null)
}

interface UseResourceFieldsReturn {
  fields: FieldConfig[]
  updateField: (fieldName: string, updates: Partial<FieldConfig>) => void
  /** The current user may only view this existing resource; every field is displayOnly. */
  isLocked: boolean
}

export const useResourceFields = ({
  record,
  template,
  propertyDefinitions,
  recordId,
  resourceTypeId,
  projectDateStore,
  fieldOptions,
}: UseResourceFieldsParams): UseResourceFieldsReturn => {
  const includeStaticFields = fieldOptions?.includeStaticFields ?? true
  const allOptional = fieldOptions?.allOptional ?? false
  const includeAllProperties = fieldOptions?.includeAllProperties ?? false
  const optionalTemplateRequirements =
    fieldOptions?.optionalTemplateRequirements ?? false
  const templatePropsOptional = allOptional || optionalTemplateRequirements
  const isMapsSurface = fieldOptions?.isMapsSurface ?? false
  const requiredRootFields = fieldOptions?.requiredRootFields
  const isPreview = fieldOptions?.isPreview ?? false

  // Read off the provider rather than the call sites, matching how the other
  // shared resource hooks reach the current user. The create defaults resolve only
  // while creating, and stay empty where no provider is mounted.
  const isCreation = recordId === 'new'
  const stores = React.useContext(MobXProviderContext) as any
  const creatingUser = isCreation ? stores?.state?.user : undefined
  const currentUserId = creatingUser?.id
  const currentUserCompanyId =
    creatingUser && stores?.userProjectsStore
      ? UserProject.getCompanyId(creatingUser, stores.userProjectsStore)
      : null

  // An existing resource is shared by every activity and map that shows it, so only its
  // owners and admins may change it; everyone else sees its values display-only, in place.
  // Creating stays open; bulk edit (no single record) is gated where its selection is made.
  const canEdit = useCanEditResource(resourceTypeId)
  const isLocked = !isCreation && !!recordId && !!record && !canEdit(record)

  const createDefaults = creatingUser
    ? getResourceDefinition(resourceTypeId)?.form?.createDefaults
    : undefined

  const builtFields = React.useMemo((): FieldConfig[] => {
    const readOnlyPropertyNames = resourceTypeId
      ? new Set(buildResourceUiConfig(resourceTypeId).readOnlyPropertyNames)
      : new Set<string>()

    const staticFields =
      includeStaticFields && resourceTypeId
        ? RESOURCE_STATIC_FIELDS[resourceTypeId] || []
        : []

    const staticFieldConfigs = staticFields.map(sf =>
      transformStaticFieldToFieldConfig(
        sf,
        record,
        readOnlyPropertyNames,
        allOptional,
      ),
    )

    // Creating outside Maps drops the resource's Maps-only root fields. Editing
    // keeps them, so a parent assigned in Maps stays visible and changeable
    // everywhere.
    const excludedRootFields =
      isCreation && !isMapsSurface
        ? new Set(
            getResourceDefinition(resourceTypeId)?.form?.mapsOnlyRootFields ??
              [],
          )
        : new Set<string>()

    const requiredRootFieldNames = new Set(requiredRootFields)
    requiredRootFieldNames.forEach(name => excludedRootFields.delete(name))

    // The site root is the top of the hierarchy: it has no parent and cannot be given one.
    // Dropped rather than disabled - an always-empty picker is the thing the card should not
    // show at all. Safe for the save: splitResourceFormData only carries keys the form
    // rendered, and locationInputOf falls back to the stored parent when `parent` is absent.
    if (
      !isCreation &&
      isProjectLocationKind(record?.data?.[LOCATION_KIND_PROPERTY])
    ) {
      excludedRootFields.add('parent')
    }

    // Mandatory means the resource says so, which is a different claim from a template
    // marking a field required - so it survives `optionalTemplateRequirements`, the flag
    // that lets an existing record be saved without satisfying requirements added to its
    // template after the fact.
    //
    // `allOptional` turns BOTH off. It marks the all-fields catalogue bulk edit and the
    // property preview build, where the fields are a menu rather than a form: requiring
    // one would demand it of every selected record, and alwaysVisible would drop it from
    // computeAddableFieldOptions - making a mandatory field the one thing bulk edit cannot
    // pick.
    const requireMandatory = (
      field: FieldConfig | null,
      isMandatoryProperty: boolean,
    ): FieldConfig | null =>
      field && isMandatoryProperty && !allOptional
        ? { ...field, required: true, alwaysVisible: true }
        : field

    const rootFieldConfigs = includeStaticFields
      ? buildRootFieldConfigs(resourceTypeId, record, excludedRootFields)
          .map(field =>
            requireMandatory(field, requiredRootFieldNames.has(field.name)),
          )
          .filter((field): field is FieldConfig => field !== null)
      : []

    // Assembled once so every return path prepends the same system fields
    // (name + root fields) and no branch can forget them.
    const systemFieldConfigs = [...staticFieldConfigs, ...rootFieldConfigs]

    // On creation, Responsible and Company start as whoever is creating the
    // record. Anything the caller already seeded wins, and the user can change
    // the value before saving. Empty values are dropped rather than seeded as
    // null: field builders treat a present-but-empty key as "assigned", which
    // reads as a deliberate blank instead of an untouched field.
    const seededData = Object.fromEntries(
      Object.entries(createDefaults || {})
        .map(([key, source]) => [
          key,
          source === 'currentUser' ? currentUserId : currentUserCompanyId,
        ])
        .filter(([, value]) => !!value),
    )
    const recordData = { ...seededData, ...(record?.data || {}) }

    // Declared above both branches: the all-properties path below is what the resource
    // card's EDIT mode uses, so leaving it out would have exempted the primary surface
    // from the rule the create path enforces.
    const mandatoryNames = new Set(
      getResourceDefinition(resourceTypeId)?.form?.mandatoryPropertyKeys ?? [],
    )

    if (includeAllProperties && propertyDefinitions?.length) {
      const propertyFields = [...propertyDefinitions]
        .filter(propertyDef => propertyDef?.name && propertyDef?.propertyType)
        .sort((a, b) => (a.position || 0) - (b.position || 0))
        .map(propertyDef => {
          const isMandatoryProperty = mandatoryNames.has(propertyDef.name)
          const field = transformPropertyToField(
            {
              propertyId: propertyDef.id || null,
              isRequired: isMandatoryProperty,
              isHidden: false,
              defaultValue: undefined,
            },
            propertyDef,
            recordData,
            readOnlyPropertyNames,
            templatePropsOptional,
            projectDateStore,
            recordId,
            resourceTypeId,
            isPreview,
          )
          return requireMandatory(field, isMandatoryProperty)
        })
        .filter(Boolean) as FieldConfig[]

      return [...systemFieldConfigs, ...propertyFields]
    }

    // Only the DEFINITIONS gate the rest: with none there is nothing to build a field
    // from. An empty template is not a stopping point - a mandatory property still has to
    // render, and a location template saved before the kind became mandatory can legally
    // hold no properties at all. Bailing here left creation with no Type field to fill and
    // a backend that rejects the save for missing a kind: a dead end, not a bad value.
    if (!propertyDefinitions?.length) {
      return systemFieldConfigs
    }

    const templateProperties = template?.properties ?? []

    const propertyDefMap = new Map(
      propertyDefinitions.filter(p => p.id).map(p => [p.id, p]),
    )

    // Properties the resource declares as part of what it IS. They survive a template
    // that hides them, and are appended when a template omits them outright.
    const isMandatory = (propertyId?: string | null) =>
      !!propertyId && mandatoryNames.has(propertyDefMap.get(propertyId)?.name)

    const sortedTemplateProps = [...templateProperties]
      .filter(
        tp => tp.propertyId && (!tp.isHidden || isMandatory(tp.propertyId)),
      )
      .sort((a, b) => (a.orderIndex || 0) - (b.orderIndex || 0))

    // A template that never listed the property at all still has to render it.
    const listedPropertyIds = new Set(
      sortedTemplateProps.map(tp => tp.propertyId),
    )
    const missingMandatoryProps = propertyDefinitions
      .filter(
        propertyDef =>
          propertyDef.id &&
          mandatoryNames.has(propertyDef.name) &&
          !listedPropertyIds.has(propertyDef.id),
      )
      .map(propertyDef => ({
        propertyId: propertyDef.id,
        isRequired: true,
        isHidden: false,
        defaultValue: undefined,
      }))

    const propertyFields = [...sortedTemplateProps, ...missingMandatoryProps]
      .map(templateProp => {
        const propertyDef = templateProp.propertyId
          ? propertyDefMap.get(templateProp.propertyId)
          : undefined
        if (!propertyDef) return null

        const isRequiredProperty = mandatoryNames.has(propertyDef.name)
        const field = transformPropertyToField(
          isRequiredProperty
            ? { ...templateProp, isRequired: true, isHidden: false }
            : templateProp,
          propertyDef,
          recordData,
          readOnlyPropertyNames,
          templatePropsOptional,
          projectDateStore,
          recordId,
          resourceTypeId,
          isPreview,
        )
        // alwaysVisible keeps it out of "Add field" and out of the removable set, the way
        // the system fields above it behave.
        return requireMandatory(field, isRequiredProperty)
      })
      .filter(Boolean) as FieldConfig[]

    return [...systemFieldConfigs, ...propertyFields]
  }, [
    record,
    template,
    propertyDefinitions,
    recordId,
    resourceTypeId,
    projectDateStore,
    includeStaticFields,
    allOptional,
    includeAllProperties,
    templatePropsOptional,
    isCreation,
    isMapsSurface,
    requiredRootFields,
    isPreview,
    createDefaults,
    currentUserId,
    currentUserCompanyId,
  ])

  const initialFields = React.useMemo(
    () =>
      isLocked
        ? builtFields.map(field => ({ ...field, displayOnly: true }))
        : builtFields,
    [builtFields, isLocked],
  )

  const [fields, setFields] = React.useState<FieldConfig[]>(initialFields)

  React.useEffect(() => {
    setFields(initialFields)
  }, [initialFields])

  const updateField = React.useCallback(
    (fieldName: string, updates: Partial<FieldConfig>) => {
      setFields(prevFields =>
        prevFields.map(field =>
          field.name === fieldName
            ? ({ ...field, ...updates } as FieldConfig)
            : field,
        ),
      )
    },
    [],
  )

  return { fields, updateField, isLocked }
}
